import { UsersThree, MagnifyingGlass, X, BellSlash } from "@phosphor-icons/react";

/**
 * Left rail of the Team Chat panel.
 * - Shows a "Create group" button + search input at the top.
 * - When the search input is populated, swaps the thread list for full-text search results.
 * - Otherwise lists DM + group threads with unread badges and mute state.
 */
export default function TeamThreadList({
  threads,
  active,
  setActive,
  search,
  setSearch,
  searchResults,
  mutedThreads,
  currentUserId,
  onCreateGroup,
}) {
  return (
    <>
      <div className="px-3 py-2 border-b border-[var(--border)] bg-[var(--bg-sidebar)] space-y-2">
        <button
          onClick={onCreateGroup}
          data-testid="team-create-group-btn"
          className="w-full flex items-center justify-center gap-1.5 bg-[var(--accent)] text-white text-[12px] font-semibold py-2 hover:opacity-90"
        >
          <UsersThree size={14} weight="bold" /> Create group
        </button>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search messages..."
            className="input pl-7 text-[12px] py-1.5"
            data-testid="team-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {searchResults !== null ? (
        <div data-testid="team-search-results">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold border-b border-[var(--border)]">
            {searchResults.length} match{searchResults.length === 1 ? "" : "es"}
          </div>
          {searchResults.length === 0 && (
            <div className="px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">No results</div>
          )}
          {searchResults.map((m) => (
            <div
              key={m.id}
              className="px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--bg-sidebar)] cursor-pointer"
              data-testid={`team-search-result-${m.id}`}
            >
              <div className="text-[12px] font-semibold">{m.from_user_name}</div>
              <div className="text-[12px] text-[var(--text-secondary)] truncate">{m.body}</div>
              <div className="text-[10px] font-mono text-[var(--text-muted)] mt-0.5">{new Date(m.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {threads.length === 0 && (
            <div className="px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">
              You&apos;re the only one here. Invite teammates from the Team page.
            </div>
          )}
          {threads.map((t) => {
            const isGroup = t.kind === "group";
            const tk = isGroup ? `group:${t.id}` : [currentUserId, t.id].sort().join(":");
            const muted = mutedThreads.has(tk);
            const label = isGroup ? t.group.name : t.user.name;
            const avatar = isGroup
              ? <div className="w-9 h-9 border border-[var(--border)] bg-[var(--accent)] text-white flex items-center justify-center"><UsersThree size={16} weight="fill" /></div>
              : (t.user.avatar_url
                  ? <img src={t.user.avatar_url} className="w-9 h-9 border border-[var(--border)]" alt="" />
                  : <div className="w-9 h-9 border border-[var(--border)] bg-white flex items-center justify-center font-display font-bold text-[14px]">{(t.user.name || "?").slice(0,1).toUpperCase()}</div>);
            const hasUnread = t.unread > 0 && !muted;
            return (
              <button
                key={`${t.kind}-${t.id}`}
                onClick={() => setActive(t)}
                data-testid={isGroup ? `team-thread-group-${t.id}` : `team-thread-${t.id}`}
                className={`relative w-full text-left px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--bg-sidebar)] flex items-center gap-3 ${active?.kind === t.kind && active?.id === t.id ? "bg-[var(--bg-sidebar)]" : ""} ${hasUnread ? "bg-[#EEF0FF]/40" : ""}`}
              >
                {hasUnread && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--accent)]" />}
                <div className="relative shrink-0">
                  {avatar}
                  {hasUnread && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[var(--accent)] border-2 border-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className={`truncate text-[14px] flex items-center gap-1.5 ${hasUnread ? "font-extrabold text-[var(--text-primary)]" : "font-semibold"}`}>
                      {isGroup && <span className="text-[10px] uppercase tracking-widest bg-[var(--accent)] text-white px-1.5 py-0.5">GROUP</span>}
                      {label}
                      {muted && <BellSlash size={12} className="text-[var(--text-muted)]" />}
                    </div>
                    {hasUnread && (
                      <span
                        className="bg-[var(--accent)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
                        data-testid={`team-unread-${t.kind}-${t.id}`}
                      >
                        {t.unread > 99 ? "99+" : t.unread}
                      </span>
                    )}
                  </div>
                  <div className={`text-[11px] truncate ${hasUnread ? "text-[var(--text-secondary)] font-medium" : "text-[var(--text-muted)]"}`}>
                    {t.last_message
                      ? `${t.last_message.from_user_id === currentUserId ? "You: " : (isGroup ? `${t.last_message.from_user_name || "—"}: ` : "")}${t.last_message.body || (t.last_message.deleted_for_all ? "(deleted)" : "")}`
                      : <span className="uppercase tracking-wider">{isGroup ? `${t.group.members?.length || 0} members` : t.user.role}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </>
      )}
    </>
  );
}
